import { RegistryProvider, useAtomInitialValues, useAtomValue } from "@effect-atom/atom-react";
import { HydrationBoundary } from "@effect-atom/atom-react/ReactHydration";
import { RpcClient } from "@effect/rpc";
import { Person } from "@fm/db/models/Person";
import { createRootRoute, HeadContent, Outlet, Scripts, useRouterState } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getCookie, getRequestHeaders } from "@tanstack/react-start/server";
import { Effect, Option, Schema } from "effect";
import { lazy, Suspense } from "react";
import { CookieConsentBanner } from "~/components/CookieConsentBanner.tsx";
import { DefaultCatchBoundary } from "~/components/DefaultCatchBoundary.tsx";
import { NotFound } from "~/components/NotFound.tsx";
import { PostHogSync } from "~/components/PostHogSync.tsx";
import { routeNeedsClerk } from "~/config/clerk-routes.ts";
import { type BrandIdentity, getRoutePrefixForHostname } from "~/config/domains.ts";
import { defaultHeadConfig, getHeadConfigForHostname, type HeadConfig } from "~/config/head.ts";
import { isPublicPagePath } from "~/config/public-pages.ts";
import { brandIdentityAtom } from "~/lib/atoms/brand-identity.ts";
import { COOKIE_CONSENT_KEY, type CookieConsent } from "~/lib/atoms/cookie-consent.ts";
import { paywallVisibleAtom } from "~/lib/atoms/paywall.ts";
import { CURRENT_USER_HYDRATION_KEY } from "~/lib/atoms/user-hydration-key.ts";
import { getClerkKeys } from "~/lib/env.ts";
import { dehydratedAtomsFromMatches, hydrateAtoms } from "~/lib/hydration.ts";
import { type BaseServerState, resolveRootServerState, seedClientServerState } from "~/lib/root-server-state.ts";
import { AuthRpcs } from "~/lib/rpc-client.ts";
import { forwardedHostFromHeaders } from "~/lib/server-auth.ts";
import { makeServerRpcLayer } from "~/lib/server-rpc.ts";
import { type HostnameContext, hostnameContextFor } from "~/middleware/hostname.ts";

/**
 * Root head config — selects the correct head config based on hostname context
 * supplied by the route's `beforeLoad` (which surfaces the hostname middleware
 * result). Falls back to FlirtMachine defaults when no context is available
 * (e.g. during tests or when the middleware did not run).
 *
 * @since 1.0.0
 */
export const rootHeadConfig = (
  ctx?: { match?: { context?: { hostname?: HostnameContext["hostname"]; identity?: BrandIdentity | undefined } } },
): HeadConfig => getHeadConfigForHostname(ctx?.match?.context?.hostname, ctx?.match?.context?.identity);

export const RootNotFound = () => <NotFound />;

/**
 * Full server state type returned from beforeLoad
 *
 * Uses the library's DehydratedAtom type for proper type compatibility
 * with HydrationBoundary component.
 */
export interface ServerState extends BaseServerState {
  hostname?: HostnameContext["hostname"];
  /** Whether the current domain maps to a known route prefix */
  domainRoutePrefix?: string | undefined;
  /** Brand identity for the requesting host (ClawInstall or Consinna); undefined outside the property. */
  identity?: BrandIdentity | undefined;
}

// Server function to fetch Clerk auth state and hydrate user atom
/* v8 ignore start -- @preserve - Server function tested through E2E tests */
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start server functions require async/await
const fetchServerState = createServerFn({ method: "GET" }).handler(async (ctx): Promise<BaseServerState> => {
  // Read the Clerk session cookie directly. Its value IS the JWT — same one
  // the API verifies locally via `verifyToken({ jwtKey })` in
  // `packages/api/src/rpc/auth.middleware.ts:117`. Previously we called
  // `auth().getToken()`, which round-trips to Clerk's API to mint a fresh
  // token; that took 1–3 s on the first SSR paint and sometimes returned
  // Unauthorized mid-warmup (see PR #717 follow-up). Reading the cookie
  // here is < 1 ms and never blocks. `serverAuth.{userId,sessionId}` is
  // not consumed by any rendered component today (only inside this fn to
  // decide whether to hydrate the user atom), so the cookie's presence is
  // the only signal we need; the user-id falls out of the API's
  // `ensureUser` response.
  const token = getCookie("__session") ?? null;

  const serverAuth = {
    userId: token ? "" : null,
    sessionId: token ? "" : null,
  };

  // Resolve the persisted consent choice from the request cookie so the banner
  // can be server-rendered into the initial HTML (CLA-702). `getCookie` returns
  // the decoded value; only the two known choices count, anything else is
  // treated as "undecided" (banner shown).
  const rawConsent = getCookie(COOKIE_CONSENT_KEY);
  const consent: CookieConsent | null = rawConsent === "accepted" || rawConsent === "declined" ? rawConsent : null;

  // If not authenticated, return early with no user data
  if (!token) {
    // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Server functions require async/await
    const hydration = await hydrateAtoms({
      [CURRENT_USER_HYDRATION_KEY]: {
        schema: Schema.Option(Person.json),
        effect: Effect.succeed(Option.none<Schema.Schema.Type<typeof Person>>()),
      },
    })(ctx);
    return {
      serverAuth,
      serverUser: null,
      consent,
      dehydratedAtoms: hydration.dehydratedAtoms,
    };
  }

  // Build a self-contained server-side RPC layer with the Clerk bearer token
  // forwarded straight from the cookie, plus the inbound brand host so the
  // internal `ensureUser` hop resolves the SAME per-brand Clerk instance the
  // token was minted by (a clawinstall `pk_live` token must be verified against
  // the clawinstall jwtKey, not the default FlirtMachine key — same brand-loss
  // fix as the SSR admin-role path).
  const rpcLayer = makeServerRpcLayer(token, forwardedHostFromHeaders(getRequestHeaders()));

  // Hydrate currentUser atom with server-fetched data. The atom in
  // `lib/atoms/user.ts` is wrapped with `hydratable({ key: "currentUser", ... })`
  // so the registry decodes this entry on first access and seeds the atom
  // with `Result.Success(Some(user))` before the client RPC fires.
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Server functions require async/await
  const hydration = await hydrateAtoms({
    [CURRENT_USER_HYDRATION_KEY]: {
      schema: Schema.Option(Person.json),
      effect: RpcClient.make(AuthRpcs).pipe(
        Effect.flatMap((client) => client.ensureUser(void 0)),
        Effect.map((user) => Option.some(user)),
        Effect.provide(rpcLayer),
        Effect.scoped,
        Effect.catchAll((error) =>
          Effect.sync(() => {
            console.error("[fetchServerState] Error fetching user:", error);
            return Option.none<Schema.Schema.Type<typeof Person>>();
          })
        ),
      ) as Effect.Effect<Option.Option<Schema.Schema.Type<typeof Person.json>>, never, never>,
    },
  })(ctx);

  return {
    serverAuth,
    serverUser: null,
    consent,
    dehydratedAtoms: hydration.dehydratedAtoms,
  };
});
/* v8 ignore stop */

/**
 * The Clerk auth shell, code-split behind a dynamic import.
 *
 * `React.lazy` makes `@clerk/*` reachable from the root only through an
 * `import()` boundary, so Rollup keeps it out of the always-loaded root graph:
 * a marketing route never renders {@link AuthGate}'s truthy branch and so never
 * loads this chunk (nor the `vendor-clerk` split it depends on).
 *
 * @since 1.0.0
 */
const AuthShell = lazy(() => import("~/components/AuthShell.tsx"));

/**
 * The site-wide impersonation banner, code-split behind a dynamic import.
 *
 * {@link ImpersonationBanner} statically imports `@clerk/*` (it reads
 * `useAuth().actor`), so reaching it only through `React.lazy(() => import(...))`
 * keeps the Clerk runtime out of the always-loaded root graph — the same
 * bundle-splitting contract the lazy {@link AuthShell} upholds (CLA-644). It is
 * rendered as a child of `AuthShell` below, so it sits under `ClerkProvider` and
 * appears on EVERY authenticated page, never on marketing routes.
 *
 * @since 1.0.0
 */
const ImpersonationBanner = lazy(() => import("~/components/ImpersonationBanner.tsx"));

/**
 * The paywall modal, code-split behind a dynamic import.
 *
 * {@link SimplePaywall} pulls the subscription checkout atom → `SubscriptionRpcClient`
 * → the Effect RPC layer. It renders only when `paywallVisibleAtom` is true (an
 * authenticated, over-limit user action), so importing it lazily keeps that RPC
 * graph out of the always-loaded `main` chunk that every marketing route
 * downloads (CLA-645).
 *
 * @since 1.0.0
 */
const SimplePaywall = lazy(() => import("~/components/SimplePaywall.tsx"));

/**
 * Mount the Clerk auth shell only for routes that need it.
 *
 * Marketing routes (`needsClerk === false`) render `children` bare — no Clerk
 * module is reached, so the lazy `AuthShell` chunk stays unloaded. Authenticated
 * routes render the children inside the lazily-imported {@link AuthShell}.
 *
 * The `Suspense` fallback is deliberately `null`, never `children`: the children
 * mount Clerk hooks/components (`<SignIn>`, `ensureUserAtom`, …) that throw
 * without a surrounding `ClerkProvider`. On the initial SSR load React preserves
 * the streamed markup and hydrates the boundary once the chunk lands (no flash);
 * the fallback is only briefly visible on a client-side navigation from a
 * marketing page into an authenticated one, while the chunk downloads.
 *
 * @since 1.0.0
 */
export interface AuthGateProps {
  readonly needsClerk: boolean;
  readonly publishableKey: string;
  readonly children: React.ReactNode;
}

export function AuthGate({ children, needsClerk, publishableKey }: AuthGateProps) {
  if (!needsClerk) {
    return <>{children}</>;
  }
  return (
    <Suspense fallback={null}>
      <AuthShell publishableKey={publishableKey}>
        <ImpersonationBanner />
        {children}
      </AuthShell>
    </Suspense>
  );
}

/* v8 ignore start -- @preserve - Route component tested through E2E tests */
export function RootComponent() {
  const context = Route.useRouteContext() as ServerState;
  // Seed the per-tab server-state cache from the hydrated SSR context (a plain
  // ref write, not an atom write — the same pattern as ClerkAuthSync's refs).
  seedClientServerState(context);

  // Gate the Clerk auth shell on the current (internal, prefix-rewritten)
  // pathname. The pathname is identical on server and client, so `AuthGate`
  // resolves to the same tree on both sides and hydration agrees. A signed-out
  // marketing visitor never mounts Clerk (see clerk-routes.ts).
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  // The dehydrated atoms to seed the registry with must come from the DEEPEST
  // matched route, not the root context: each `/orca/*` route's `beforeLoad`
  // accumulates its SSR seed onto its parent's `dehydratedAtoms`, so the root
  // match carries only the current-user seed and every child-route seed lives
  // in a descendant match. Sourcing from the leaf keeps the child atoms' own
  // RPC effect from running (and failing) during SSR — the server renders the
  // seeded content instead of the error frame (OL-20).
  const dehydratedAtoms = useRouterState({ select: (state) => dehydratedAtomsFromMatches(state.matches) });

  // Select the brand's Clerk instance from the hostname context. ClawInstall
  // runs on its own instance in production; everything else uses the default.
  // (Resolved identically on server and client, so SSR and hydration agree.)
  const { publishableKey } = getClerkKeys(
    context.hostname ?? { isClawInstall: false },
  );

  // No-JS font fallback for THIS brand. The css2 font stylesheets are injected
  // asynchronously via the head stylesheet-loader script (non-render-blocking);
  // when JS is disabled that script never runs, so RootDocument renders these
  // hrefs as blocking `<link>`s inside a `<noscript>` so no-JS users still get
  // the webfonts. (The app stylesheet needs no fallback: it is a real
  // render-blocking `<link>` in the head config.) Resolved from the same
  // hostname context the head config uses, so the fallback matches the brand.
  const { noscriptStylesheetHrefs } = getHeadConfigForHostname(context.hostname);

  // Domain-specific layouts are handled by layout routes (clawinstall.tsx).
  // Root always renders the same shell — each domain's layout route customizes its content.
  return (
    <RegistryProvider>
      <HydrationBoundary state={dehydratedAtoms}>
        <RootDocument
          noscriptStylesheetHrefs={noscriptStylesheetHrefs}
          initialConsent={context.consent ?? null}
          identity={context.identity ?? null}
          showCookieBanner={!isPublicPagePath(pathname)}
        >
          <AuthGate needsClerk={routeNeedsClerk(pathname)} publishableKey={publishableKey}>
            <Outlet />
          </AuthGate>
        </RootDocument>
      </HydrationBoundary>
    </RegistryProvider>
  );
}
/* v8 ignore stop */

/**
 * Client-only paywall container
 *
 * Separates the paywall atom subscription from the document root
 * to avoid SSR streaming issues with hook calls in the html element.
 * This component only renders on the client after hydration.
 *
 * @since 1.0.0
 */
/* v8 ignore start -- @preserve - Client-only component */
function PaywallContainer() {
  const paywallVisible = useAtomValue(paywallVisibleAtom);

  // Don't render during SSR
  if (typeof window === "undefined") {
    return null;
  }

  return paywallVisible
    ? (
      <Suspense fallback={null}>
        <SimplePaywall />
      </Suspense>
    )
    : null;
}
/* v8 ignore stop */

export function RootDocument({
  children,
  identity = null,
  initialConsent = null,
  noscriptStylesheetHrefs = defaultHeadConfig().noscriptStylesheetHrefs,
  showCookieBanner = true,
}: {
  children: React.ReactNode;
  /**
   * Brand identity resolved from the request host (`null` outside the
   * ClawInstall property). Seeds {@link brandIdentityAtom} so descendants read
   * the correct identity on the first (SSR) render — no hydration mismatch, no
   * `useState`/`useEffect`. Mirrors {@link CookieConsentBanner}'s seeding.
   */
  identity?: BrandIdentity | null;
  /**
   * The css2 font stylesheet hrefs rendered as blocking `<link>`s inside a
   * `<noscript>` so no-JS users still get the webfonts (the async loader script
   * never runs for them). Defaults to the FlirtMachine set when a caller omits it.
   */
  noscriptStylesheetHrefs?: Array<string>;
  /**
   * The visitor's server-resolved cookie-consent choice (`null` = undecided),
   * passed to {@link CookieConsentBanner} so it is server-rendered into the
   * initial HTML. Defaults to `null` (banner shown) when a caller omits it.
   */
  initialConsent?: CookieConsent | null;
  /**
   * Whether the global cookie-consent banner renders. `false` on the
   * clawinstall-public marketing pages, whose own bottom-left `CookieBanner`
   * replaces it (see {@link isPublicPagePath}). Defaults to `true`.
   */
  showCookieBanner?: boolean;
}) {
  // Seed the per-request brand identity on first render (idempotent), so the SSR
  // markup and the first client render agree — same pattern as CookieConsentBanner.
  useAtomInitialValues([[brandIdentityAtom, identity]]);

  return (
    <html lang="en">
      <head>
        <HeadContent />
        <noscript>
          {noscriptStylesheetHrefs.map((href) => <link key={href} rel="stylesheet" href={href} />)}
        </noscript>
      </head>
      <body suppressHydrationWarning>
        {
          /* Clerk is mounted per-route by RootComponent's AuthGate (only auth
          routes), so ClerkAuthSync lives inside AuthShell now — not here. This
          keeps `@clerk/*` out of the always-loaded root graph. PostHogSync is
          brand-global and Clerk-independent, so it stays at the document root. */
        }
        <PostHogSync>
          {children}
        </PostHogSync>
        {/* v8 ignore next -- @preserve Paywall visibility tested in SimplePaywall.test.tsx */}
        <PaywallContainer />
        {
          /* Cookie consent — global so every domain in the multi-domain setup is covered.
            Server-rendered into the initial HTML: `initialConsent` is resolved from the
            request cookie in `fetchServerState`, so the banner's visibility is identical
            on the server and on the first client render (both derive from the same
            cookie) — no hydration mismatch — and it paints with first paint instead of
            after hydration, which is what made it the LCP element (CLA-702). */
        }
        {showCookieBanner ? <CookieConsentBanner initialConsent={initialConsent} /> : null}
        <Scripts />
      </body>
    </html>
  );
}

export const Route = createRootRoute({
  head: rootHeadConfig,
  errorComponent: DefaultCatchBoundary,
  notFoundComponent: RootNotFound,
  component: RootComponent,
  /* v8 ignore start -- @preserve - Route beforeLoad tested through E2E tests */
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start route hooks require async/await
  beforeLoad: async (args): Promise<ServerState> => {
    // Extract context from middleware - TanStack Start passes middleware context in args.context
    const argsWithContext = args as unknown as {
      context?: HostnameContext;
      serverContext?: HostnameContext;
    };
    const context = argsWithContext.context;
    const serverContext = argsWithContext.serverContext;

    // Use whichever has hostname data
    const hostnameData = context?.hostname ?? serverContext?.hostname ??
      { full: "", isClawInstall: false, isOrca: false, isFlirtMachine: false };

    // Resolve hostname — server context first, client-side fallback
    let fullHostname = String(hostnameData.full || "");
    if (!fullHostname && typeof window !== "undefined") {
      fullHostname = window.location.hostname;
    }

    const domainRoutePrefix = getRoutePrefixForHostname(fullHostname);
    const hostnameContext = hostnameContextFor(fullHostname);

    // Server state is fetched on the server (first paint) and at most once per
    // tab on the client — never on intent preloads, never on every navigation.
    // oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start route hooks require async/await
    const state = await resolveRootServerState(
      { isServer: typeof window === "undefined", preload: args.preload === true },
      () => fetchServerState(),
    );
    return { ...state, hostname: hostnameContext, domainRoutePrefix, identity: hostnameContext.identity };
  },
  /* v8 ignore stop */
});
